/**
 * Orchestra AI Platform - Project Card Component
 * 
 * Card component for displaying project information
 * 
 * @fileoverview Project card with actions and metadata
 */

import PropTypes from 'prop-types';
import { format } from 'date-fns';

/**
 * @typedef {Object} Project
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {('workflow'|'api_integration'|'cross_subject')} type
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} last_opened_at
 */

/**
 * Project Card Component
 * @param {Object} props
 * @param {Project} props.project - Project data
 * @param {Function} props.onOpen - Open handler
 * @param {Function} props.onArchive - Archive handler
 * @param {Function} props.onDelete - Delete handler
 */
export function ProjectCard({ project, onOpen, onArchive, onDelete }) {
  const typeLabels = {
    workflow: 'Workflow',
    api_integration: 'API Integration',
    cross_subject: 'Cross-Subject',
  };

  const typeColors = {
    workflow: 'bg-blue-100 text-blue-800',
    api_integration: 'bg-green-100 text-green-800',
    cross_subject: 'bg-purple-100 text-purple-800',
  };

  /**
   * Handle context menu
   * @param {React.MouseEvent} event
   */
  const handleContextMenu = (event) => {
    event.preventDefault();
    // TODO: Show context menu
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
      onClick={onOpen}
      onContextMenu={handleContextMenu}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-gray-900 flex-1">{project.name}</h3>
        <span
          className={`px-2 py-1 text-xs font-semibold rounded ${typeColors[project.type] || 'bg-gray-100 text-gray-800'}`}
        >
          {typeLabels[project.type] || project.type}
        </span>
      </div>

      {/* Description */}
      {project.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
      )}

      {/* Metadata */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
        <span>
          Updated {project.updated_at ? format(new Date(project.updated_at), 'MMM d, yyyy') : 'Never'}
        </span>
        {project.last_opened_at && (
          <span>
            Opened {format(new Date(project.last_opened_at), 'MMM d')}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onOpen}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Open
        </button>
        <button
          onClick={onArchive}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          title="Archive"
        >
          📦
        </button>
        <button
          onClick={onDelete}
          className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
          title="Delete"
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

ProjectCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    type: PropTypes.oneOf(['workflow', 'api_integration', 'cross_subject']).isRequired,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    last_opened_at: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
};

